import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Client, ClientFormData, ClientFilters } from '../types/client-types';

// Tipos de acciones para las sagas
export interface CreateClientRequest {
  clientData: ClientFormData;
}

export interface UpdateClientRequest {
  id: string;
  clientData: Partial<ClientFormData>;
}

export interface GetClientsRequest {
  filters?: ClientFilters;
  endpoint?: string; // Endpoint personalizado opcional
}

export interface GetClientByIdRequest {
  id: string;
}

export interface DeleteClientRequest {
  id: string;
}

export interface UpdateClientStatusRequest {
  id: string;
  status: string;
}

// Estado inicial
export interface ClientState {
  clients: Client[];
  currentClient: Client | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

const initialState: ClientState = {
  clients: [],
  currentClient: null,
  loading: false,
  error: null,
  pagination: null,
};

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    // Acciones que disparan sagas
    createClientRequest: (state, _action: PayloadAction<CreateClientRequest>) => {
      state.loading = true;
      state.error = null;
    },
    updateClientRequest: (state, _action: PayloadAction<UpdateClientRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getClientsRequest: (state, _action: PayloadAction<GetClientsRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getClientByIdRequest: (state, _action: PayloadAction<GetClientByIdRequest>) => {
      state.loading = true;
      state.error = null;
    },
    deleteClientRequest: (state, _action: PayloadAction<DeleteClientRequest>) => {
      state.loading = true;
      state.error = null;
    },
    updateClientStatusRequest: (state, _action: PayloadAction<UpdateClientStatusRequest>) => {
      state.loading = true;
      state.error = null;
    },

    // Acciones que actualizan el estado (llamadas desde sagas)
    createClientSuccess: (state, action: PayloadAction<Client>) => {
      state.loading = false;
      state.clients.unshift(action.payload);
      state.error = null;
    },
    updateClientSuccess: (state, action: PayloadAction<Client>) => {
      state.loading = false;
      const index = state.clients.findIndex(client => client.id === action.payload.id);
      if (index !== -1) {
        state.clients[index] = action.payload;
      }
      if (state.currentClient?.id === action.payload.id) {
        state.currentClient = action.payload;
      }
      state.error = null;
    },
    getClientsSuccess: (state, action: PayloadAction<{
      clients: Client[];
      pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>) => {
      state.loading = false;
      state.clients = action.payload.clients;
      state.pagination = action.payload.pagination || null;
      state.error = null;
    },
    getClientByIdSuccess: (state, action: PayloadAction<Client>) => {
      state.loading = false;
      state.currentClient = action.payload;
      state.error = null;
    },
    deleteClientSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.clients = state.clients.filter(client => client.id !== action.payload);
      if (state.currentClient?.id === action.payload) {
        state.currentClient = null;
      }
      state.error = null;
    },
    updateClientStatusSuccess: (state, action: PayloadAction<Client>) => {
      state.loading = false;
      const index = state.clients.findIndex(client => client.id === action.payload.id);
      if (index !== -1) {
        state.clients[index] = action.payload;
      }
      if (state.currentClient?.id === action.payload.id) {
        state.currentClient = action.payload;
      }
      state.error = null;
    },
    clientFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearClientError: (state) => {
      state.error = null;
    },
    clearCurrentClient: (state) => {
      state.currentClient = null;
    },
    clearClients: (state) => {
      state.clients = [];
      state.currentClient = null;
      state.pagination = null;
      state.error = null;
    },
  },
});

export const {
  createClientRequest,
  updateClientRequest,
  getClientsRequest,
  getClientByIdRequest,
  deleteClientRequest,
  updateClientStatusRequest,
  createClientSuccess,
  updateClientSuccess,
  getClientsSuccess,
  getClientByIdSuccess,
  deleteClientSuccess,
  updateClientStatusSuccess,
  clientFailure,
  clearClientError,
  clearCurrentClient,
  clearClients,
} = clientSlice.actions;

export default clientSlice.reducer;
